import Vue from 'vue';
import Vuex from 'vuex';

// Modules
import spotlightModule from '@/modules/spotlight/store';
import spotlightSubscriber from '@/modules/spotlight/store/subscribers';
import { ProductMap } from '@/utils/productMap';
import helperFunctions from '@/utils/helperFunctions';
import productRoutes from '@/utils/enums/productRoutes';
import referralType from '@/utils/enums/referralType';
import vehicleModule from './vehicle';
import userModule from './user';
import businessModule from './business';
import leadModule from './lead';
import testDriveModule from './testDrive';
import creditModule from './credit';
import tradeModule from './trade';
import usCreditModule from './usCredit';
import reviewsModule from './reviews';
import metricsModule from './metrics';
import insuranceModule from './insurance';
import analyticsModule from './analytics';
import ecommerceV2Module from './ecommerceV2';
import paymentCalculatorModule from './paymentCalculator';
import spinModule from './spin';
import buildAndPriceModule from './buildAndPrice';

// Subscribers
import vehicleSubscriber from './subscribers/vehicle';
import UsCreditSubscriber from './subscribers/usCredit';
import insuranceSubscriber from './subscribers/insurance';
import reviewsSubscriber from './subscribers/reviews';
import testDriveSubscriber from './subscribers/testDrive';
import spinSubscriber from './subscribers/spin';

// Analytics subscribers
import AVAnalyticsSubscriber from './subscribers/AVAnalytics/analytics';
import ShiftDigitalAnalyticsSubscriber from './subscribers/AVAnalytics/shiftDigital';

import { AVRProducts, DeviceType } from '@/utils/enums';
import TradeInSource from '@/utils/enums/tradeInSource';

Vue.use(Vuex);

const storeInstance = new Vuex.Store({
  strict: false,
  plugins: [
    vehicleSubscriber,
    UsCreditSubscriber,
    insuranceSubscriber,
    reviewsSubscriber,
    testDriveSubscriber,
    spotlightSubscriber,
    spinSubscriber,
    // Analytics
    AVAnalyticsSubscriber,
    ShiftDigitalAnalyticsSubscriber,
  ],
  modules: {
    user: userModule,
    vehicle: vehicleModule,
    business: businessModule,
    lead: leadModule,
    credit: creditModule,
    trade: tradeModule,
    usCredit: usCreditModule,
    reviews: reviewsModule,
    testDrive: testDriveModule,
    metrics: metricsModule,
    insurance: insuranceModule,
    analytics: analyticsModule,
    spotlight: spotlightModule,
    ecommerceV2: ecommerceV2Module,
    paymentCalculator: paymentCalculatorModule,
    spin: spinModule,
    buildAndPrice: buildAndPriceModule,
  },
  state: {
    dhs: {
      products: {
        reviews: {
          name: 'reviews',
          state: '',
        },
        trade: {
          name: 'trade-in',
          state: '',
        },
        credit: {
          name: 'credit',
          state: '',
        },
        insurance: {
          name: 'insurance',
          state: '',
        },
        testDrive: {
          name: 'testDrive',
          state: '',
        },
        spin: {
          name: 'spin',
          state: '',
        },
      },
    },
    isLoading: false,
    askingToClose: false,
    routeList: [],
    experiment: {},
    initialStoreState: {},
    appComponentKey: 0,
    isInitialized: false,
    initializedBySdk: false,
    entryProductName: '',
  },
  getters: {
    getLoadingBoolean: state => state.isLoading,
    getRouteName: state => state.route.name,
    getProductHomeState: state => state.dhs.products,
    getShowingCloseDialog: state => state.askingToClose,
    getNavButtonType: rootState => {
      if (rootState.route.name === 'drive-choose-date-mobile' || rootState.route.name === 'drive-choose-date-desktop') {
        if (
          rootState.testDrive.testDriveAppRoute === 'selectLocation' ||
          rootState.testDrive.testDriveAppRoute === 'home'
        ) {
          return 'back';
        }
      }
      if (
        rootState.route.path.includes('personalized-payment/step-one') &&
        rootState.business.info.ecomV2Settings.showExploreCalculatorButton === false
      ) {
        return false;
      }
      return rootState.route.meta.headerIcon;
    },
    getProductHomeRoute: (state, getters) => product => {
      let path;
      const { tradeAutosEnabled, tradeSREnabled } = state.business.info.integrationSettings;
      const tradeInCarfaxEnabled = state.business.info.tradeInSource === TradeInSource.CARFAX;
      const { vin } = state.route.query;
      switch (product) {
        case AVRProducts.CREDIT:
          path = getters.getCreditHomeRoute;
          break;
        case AVRProducts.TRADE:
          switch (getters.getCurrentBaseRoute) {
            case DeviceType.DESKTOP:
              path = 'tradein/vehicle-autocomplete';
              break;
            default:
              path = 'tradein/year';
          }
          if (tradeInCarfaxEnabled) {
            path = 'tradein/carfax-basic-form';
          }

          if (tradeAutosEnabled && tradeSREnabled) {
            path = 'tradein/select-trade-vehicle-type';
          }
          if (!tradeAutosEnabled && tradeSREnabled) {
            path = 'tradein/trade-vehicle-details';
          }

          if (state.dhs.products.trade.state === 'complete') {
            path = productRoutes.TRADE_REPORT;
          }
          break;
        case AVRProducts.TEST_DRIVE:
          switch (getters['testDrive/testDriveAppRoute']) {
            case 'dealership':
              path = 'test-drive/choose-date';
              break;
            case 'selectLocation':
              path = 'test-drive/choose-location';
              break;
            default:
              path = 'test-drive/test-drive-from-home';
          }
          break;
        case AVRProducts.INSURANCE:
          switch (getters.getCurrentBaseRoute) {
            case DeviceType.DESKTOP:
              if (vin) {
                path = 'insurance/insurance-application/personalform';
              } else {
                path = 'insurance/insurance-year';
              }
              break;
            default:
              path = 'insurance/disclaimer';
          }
          break;
        case AVRProducts.ACQUIRE:
          path = 'acquire/carfax-basic-form';
          break;
        default:
      }

      if (!path) {
        const productData = ProductMap.find(value => value.name === product);
        path = productData ? productData.route : null;
      }
      const countryPrefix = state.route.params.country ? '/us' : '';
      return path ? `${countryPrefix}/${getters.getCurrentBaseRoute}/${path}` : null;
    },
    getCreditHomeRoute: (state, getters) => {
      const isHalfComplete = state.dhs.products.credit.state === 'half-complete';
      const displaySelectVehicleTypePage = getters['business/displaySelectVehicleTypePage'];
      if (getters.getCurrentBaseRoute === DeviceType.MOBILE) {
        return isHalfComplete
          ? 'credit/credit-form/credit-results'
          : displaySelectVehicleTypePage
          ? 'credit/select-vehicle-type'
          : 'credit/disclaimer';
      }
      return isHalfComplete
        ? 'credit/credit-results'
        : displaySelectVehicleTypePage
        ? 'credit/select-vehicle-type'
        : 'credit/credit-form/creditform1';
    },
    getCurrentProduct: rootState => helperFunctions.getProductFromRoute(rootState.route.path),
    getCurrentBaseRoute: rootState => {
      if (rootState.route.path.includes('/embed/')) {
        return 'embed';
      }
      if (rootState.route.path.includes('/desktop/')) {
        return 'desktop';
      }
      if (rootState.route.path.includes('/mobile/')) {
        return 'mobile';
      }
      return null;
    },
    getIsInitialized: state => state.isInitialized,
    getReferralType: rootState => (rootState.route.query.referralType ? rootState.route.query.referralType : null),
    nativeMobileIntegration: (state, getters) =>
      getters.getReferralType === referralType.THIRD_PARTY_MOBILE_APPLICATION,
    nativeMobileDeepLink: rootState => {
      try {
        const { deepLink } = rootState.route.query;
        // base64 and url decode
        const decodedDeepLink = deepLink ? atob(decodeURIComponent(deepLink)) : null;
        if (deepLink && decodedDeepLink) {
          return new URL(decodedDeepLink);
        }
        return null;
      } catch (e) {
        return null;
      }
    },
  },
  mutations: {
    SET_LOADING(state, data) {
      if (state.isLoading !== data) {
        state.isLoading = data;
      }
    },
    SET_INITIAL_STORE_STATE(state, data) {
      state.initialStoreState = JSON.parse(JSON.stringify(data));
      state.initialStoreState.initialStoreState = JSON.parse(JSON.stringify(data));
    },
    PREPARE_TO_RESET_VUE_STATE(state, data) {
      // This is to preserve the route object inside the store state, when using replaceState to reset the state.
      const stateData = data;
      stateData.route = JSON.parse(JSON.stringify(state.route));
      state.initialStoreState = JSON.parse(JSON.stringify(data));
      state.initialStoreState.initialStoreState = JSON.parse(JSON.stringify(data));
    },
    UPDATE_APP_COMPONENT_KEY(state) {
      state.appComponentKey += 1;
    },
    SET_CLOSING(state, data) {
      state.askingToClose = data;
    },
    ADD_ROUTE(state, data) {
      state.routeList.push(data);
    },
    UPDATE_ROUTE_LIST(state, { to, from }) {
      const index = state.routeList.indexOf(from);
      if (index !== -1) {
        state.routeList[index] = to;
      }
    },
    SET_EXPERIMENT(state, [key, value]) {
      state.experiment[key] = value;
    },
    SET_DHS_STATE(state, [product, value]) {
      state.dhs.products[product].state = value;
    },
    SET_IS_INITIALIZED(state, data) {
      state.isInitialized = data;
    },
    SET_INITIALIZED_BY_SDK(state, data) {
      state.initializedBySdk = data;
    },
    SET_ENTRY_PRODUCT_NAME(state, data) {
      state.entryProductName = data;
    },
  },
  actions: {
    toggleIsLoading({ commit, state }) {
      commit('SET_LOADING', !state.isLoading);
    },
    // eslint-disable-next-line no-unused-vars
    goHome({ state }, { router, homeRoute }) {
      router.push({ path: homeRoute });

      // a trigger for when they go back to the DHS
      window.parent.postMessage('return-to-home-state', '*');
    },
    goBack(context, router) {
      router.go(-1);
    },
  },
});
// This store is for private use in the application
export const privateStoreInstance = new Vuex.Store({
  state: {
    socialSecurityNumber: '',
  },
  mutations: {
    /**
     * @param state {object}
     * @param data {string}
     */
    SET_SOCIAL_SECURITY_NUMBER(state, data) {
      if (typeof data !== 'string' || data.length !== 9 || !/^\d+$/.test(data)) {
        throw new Error('data should be a string with length 9');
      }
      state.socialSecurityNumber = data;
    },
  },
});

storeInstance.commit('SET_INITIAL_STORE_STATE', JSON.parse(JSON.stringify(storeInstance.state)));

export default storeInstance;
